import type { AutoClickApplyFlag } from '../utils/autoClickApplyUtils';

import {
    AvroOptional,
    log as logReceivedFlag,
} from '@indeed/signals-schema/com/indeed/signals/schema/profile/MobRcvFlag';
import { useEffect, useRef } from 'react';

/**
 * sessionStorage key matching the mobile web `DidYouApplyTracker` format.
 * Prevents auto-click from firing again if the user navigates back to the
 * same job within the same browser session.
 */
function getStorageKey(jobKey: string): string {
    return `didyouapply-click-${jobKey}`;
}

function hasAlreadyAutoClicked(jobKey: string): boolean {
    try {
        return window.sessionStorage.getItem(getStorageKey(jobKey)) !== null;
    } catch (error) {
        console.error('[useGFJAutoClickApply] Failed to read sessionStorage:', error);
        return false;
    }
}

function recordAutoClick(jobKey: string): void {
    try {
        window.sessionStorage.setItem(getStorageKey(jobKey), `${Date.now()}`);
    } catch (error) {
        console.error('[useGFJAutoClickApply] Failed to write sessionStorage:', error);
    }
}

/**
 * Emits a `mobRcvFlag` LogRepo entry for auto-click-apply events. The `flag`
 * value distinguishes Indeed Apply (`autoClickApplyIA`) from non-IA
 * (`autoClickApplyNonIA`) so the two can be analyzed separately in the
 * `mobRcvFlag` Imhotep dataset. Both names are registered as `ReceivedFlagType`
 * values on the mweb server.
 *
 * The underlying signals_transport flushes via `sendBeacon` on
 * `visibilitychange:hidden`, so the request survives the `window.location.href`
 * navigation that immediately follows.
 */
function logAutoClickApplyEvent(
    mobtk: string,
    autoClickApplyFlag: AutoClickApplyFlag,
): void {
    try {
        logReceivedFlag({
            mobtk,
            parentEventType: 'mobviewjob',
            flag: AvroOptional.string(autoClickApplyFlag),
            ts: AvroOptional.long(Date.now()),
        });
    } catch (error) {
        console.error('[useGFJAutoClickApply] Failed to log auto-click apply event:', error);
    }
}

/**
 * Hook that auto-clicks the apply button for users arriving from Google for Jobs.
 *
 * On web, Indeed Apply and third-party apply buttons render as anchor tags, so
 * "auto-clicking" means programmatically navigating to the apply URL.
 *
 * The auto-click variant (`autoClickApplyIA` / `autoClickApplyNonIA`) is
 * resolved upstream on the `ViewjobOneGraphModel` so this hook stays agnostic
 * to whether the data came from the legacy `ViewJobClientSideModel` or a
 * future OneGraph query.
 *
 * Deduplication uses sessionStorage keyed by jobKey (matching the mobile web
 * `DidYouApplyTracker` pattern) so that browser-back navigation does not
 * trigger an infinite redirect loop. A secondary in-memory ref guard prevents
 * duplicate fires within a single mount.
 *
 * The hook fires at most once per session per job.
 */
export function useGFJAutoClickApply(
    applyUrl: string | null | undefined,
    mobtk: string | undefined,
    jobKey: string | undefined,
    autoClickApplyFlag: AutoClickApplyFlag | null | undefined,
): void {
    const hasAutoClicked = useRef(false);

    useEffect(() => {
        const shouldAutoClick = autoClickApplyFlag != null;
        if (!shouldAutoClick || !applyUrl || !jobKey) {
            return;
        }
        if (hasAutoClicked.current) {
            return;
        }
        if (hasAlreadyAutoClicked(jobKey)) {
            return;
        }

        hasAutoClicked.current = true;
        recordAutoClick(jobKey);

        if (mobtk) {
            logAutoClickApplyEvent(mobtk, autoClickApplyFlag);
        }

        window.location.href = applyUrl;
    }, [autoClickApplyFlag, applyUrl, mobtk, jobKey]);
}
